import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ProjectElement } from '../../../resources/element/project-element.interface';
import { ConstantsService } from '../../../utilities/constants/constants.service';
import { ConstantsStatusType } from '../../../utilities/constants/constants.interface';
import { ElementService } from '../../../resources/element/element.service';
import { AuthService } from '../../../auth/auth.service';
import { TranslateService } from '../../../utilities/translate/translate.service';
import { ElementSidebarService } from '../../../resources/element/element-sidebar/element-sidebar.service';
import { ModalService } from '../../../services/modal/modal.service';
import { ProjectInfoService } from '../project-info.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AttachmentService } from '../../../services/attachment/attachment.service';
import { Editor } from 'tinymce';
import { TinymceService } from '../../../services/tinymce/tinymce.service';

/**
 * Element component.
 */
@Component({
  selector: 'sg2-project-info-element',
  templateUrl: './project-info-element.component.html',
  styleUrls: ['./project-info-element.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectInfoElementComponent implements OnDestroy, OnInit {

  /** Current element. */
  @Input() public element: ProjectElement;

  /** Current editor status. */
  public editorActive = false;

  /** Unsubscribe hook. */
  private destroy$ = new Subject();

  /** Whether the current value is edited. */
  public edited = false;

  /** Active editor instane. */
  private editor: Editor;

  /** Activate inline edit mode. */
  public editInlineActive = false;

  /** Copy of the element value */
  public value: ProjectElement['value'];

  /** Copy of the element's value. */
  private originalValue: ProjectElement['value'];

  /** The active alement's status constant. */
  public status: ConstantsStatusType;

  /** TinyMCE config. */
  public tinyMCEConfig = {};

  constructor(
    private attachmentService: AttachmentService,
    public authService: AuthService,
    private constantsService: ConstantsService,
    private elementService: ElementService,
    private elementSidebarService: ElementSidebarService,
    private modalService: ModalService,
    private projectInfoService: ProjectInfoService,
    private tinymceService: TinymceService,
    private translateService: TranslateService
  ) { }

  ngOnInit() {
    this._initTinyMCE();
    this._getStatus();
    this._onInlineModeChange();
    this.element.value = this.tinymceService.sanitizeInput(this.element.value);
    this._setValue();
    this._isEdited();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Activate the editor.
   *
   * Also create a copy of the current element value, so it's possible to check for
   * changes on submitting the value.
   *
   * @note There seems to be a bug where drag-dropping images seems to reactivate
   *  the inline editor, which causes the the *original* and *current* value to be
   *  the same. This prevents the value from being saved (it's only saved when the
   *  value is different). To prevent this, the original value is only being set on
   *  the first activation (Saving a value resets this check).
   */
  public activateInline(): void {
    if (!this.editorActive) {
      this.originalValue = this.value;
    }
    this.editorActive = true;
  }

  /**
   * Toggle inline edit.
   */
  public editInline(): boolean {
    return this.editInlineActive;
  }

  /**
   * Generate tooltip for comment icon.
   *
   * @returns The generated tooltip.
   */
  public getCommentTooltip(): string {
    const comments = this.element?.commentCount || 0;
    return `${ comments } ${ comments === 1
      ? this.translateService.getLowerCase('comment')
      : this.translateService.getPluralLowerCase('comment') }`;
  }

  /**
   * Generate element id based on module/submodule/element.
   *
   * Used for scrolling to the element when creating a new element, or when selecting
   * it from the tree view.
   */
  public getElementId(): string {
    return `${ this.element.moduleId }-${ this.element.submoduleId }-${ this.element._id }`;
  }

  /**
   * Open edit modal.
   */
  public openModal(): void {
    this.modalService.openTinymce(this.element).afterClosed().subscribe(results => {
      if (results && results !== this.value) {
        this.elementService.setValue(this.element, results);
      }
    });
  }

  /**
   * Restore template value.
   */
  public restoreTemplateValue(): void {
    this.modalService.openRestoreParagraph(this.element).afterClosed().subscribe(qualityLevel => {
      if (qualityLevel) {
        const template = this.element.templateValues.find(tv => tv.quality === qualityLevel);
        this.submitValue(template.value);
      }
    });
  }

  /**
   * Show logs and history.
   *
   * @param element The element to open the log for.
   */
  public showLogs(): void {
    this.elementSidebarService.open(this.element, 'logs');
  }

  /**
   * Submit editor value.
   *
   * Before sending to the API/store, check if the value has changed. If the value is empty,
   * change it to *'Empty paragraph'* .
   *
   * @note There's a bug in the tinymce-angular lib where the model is not updated after the
   *  upload of a dropped image is completed. To make sure `this.value` contains the updated
   *  img-tag, getContents() is called on the editor instead of the value emitted on blur.
   *
   * @param [value] Optional value when it need to be set explicitly. When not used, the
   *  current editor value is used.
   */
  public submitValue(value?: ProjectElement['value']): void {
    this.value = value || this.tinymceService.sanitizeOutput(this.editor.getContent());

    if (this.value !== this.originalValue) {
      if (this.value.trim() === '') {
        this.value = `<p>${ this.translateService.get('emptyElement') }</p>`;
      }
      this.elementService.setValue(this.element, this.value);
      this.originalValue = this.value;
    }
    this._setInactive();
  }

  /**
   * Set element title.
   *
   * The title is only submitted when changed. When emptied, the titel is
   * automatically set to 'Algemeen'.
   *
   * @param $event The event that fires on submit.
   */
  public submitTitle($event: KeyboardEvent): void {
    $event.preventDefault();
    let title = ($event.target as HTMLSpanElement).innerText.trim();
    if (title === '') {
      title = 'Algemeen';
    }
    if (this.element.name !== title) {
      this.elementService.setName(this.element, title);
    }
  }

  /**
   * Get the status constant for the element's status.
   */
  private _getStatus(): void {
    this.status = this.constantsService.getStatusType(this.element.status);
  }

  /**
   * Initialize TinyMCE config.
   *
   * @note `extended_valid_elements` is there to prevent replacing <b> by <strong>,
   *  and <i> by <em> on editing, since it would wrongly trigger isChanged/DiffChecker.
   * @note `formats` is there for consistency with template values.
   */
  private _initTinyMCE(): void {
    this.tinyMCEConfig = {
      branding: false,
      formats: {
        bold: { inline: 'strong' },
        italic: { inline: 'i' }
      },
      fixed_toolbar_container: '#toolbar-container-tinymce',
      images_reuse_filename: true,
      images_upload_handler: (blobInfo, success, failure) => {
        this.attachmentService.uploadImageElement(blobInfo.filename(), blobInfo.base64()).subscribe(resolve => {
          success(`/api/project/v2/${ this.element.projectId }/element/image/${ resolve.location }`);
        });
      },
      init_instance_callback: (editor: Editor) => {
        this.editor = editor;
      },
      menubar: false,
      nonbreaking_force_tab: true,
      paste_data_images: true,
      plugins: 'paste table lists textcolor nonbreaking code',
      toolbar: 'fontselect | fontsizeselect | bold italic underline strikethrough '
        + '| forecolor backcolor | bullist numlist | alignleft aligncenter alignright alignjustify | table'
        + (this.authService.isAdmin() ? ' | code' : ''),
      extended_valid_elements: 'b,strong,em,i'
    };
  }

  /**
   * Check if the current element value is edited.
   *
   * Checks the difference *after* patching both the template and the current values.
   * Sometimes 'invisible' characters are added, such as spaces at the beginning/end
   * or empty <p> tags. When this is the only difference, a paragraph is *not* marked
   * as edited.
   *
   * @note Does not work for custom elements, since they have no template values.
   *  Custom values should not show the edited/restore label.
   */
  private _isEdited(): void {
    const template = this.element.templateValues
      && this.element.templateValues.find(tv => tv.quality === this.element.quality);
    this.edited = template && (this.element.value !== this.tinymceService.sanitizeInput(template.value));
  }

  /**
   * On inline edit mode toggle.
   */
  private _onInlineModeChange(): void {
    this.projectInfoService.inlineEdit$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(active => {
      this.editInlineActive = active;
    });
  }

  /**
   * Create a copy of the element value, to make sure the editor doesn't use a reference.
   *
   * @note Note sure if an actual reference is used, but it does seem to improve performance
   *  a bit.
   */
  private _setValue() {
    this.value = this.element.value;
  }

  /**
   * Deactivate the editor.
   *
   * @todo There is an issue where removing the element with the editor from the DOM, without
   *  destroying the editor first. The editor crashes, and activating a second editor does not
   *  work. This issue is addressed in TinyMCE v5, where it's possible to remove a specific
   *  editor from the DOM. Somehow, the editor is still on V4, probably due to the legacy pages
   *  still using V4. A ticket is made to solve this: SPEC-2743.
   *  For now, simply leave the DOM element intact, it should not hurt performance that much.
   *  The problem is not having lots of open editors, the problem is loading 100s of editors
   *  at the init phase.
   */
  private _setInactive(): void {
    // this.editorActive = false;
  }
}
